import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useTrendingTags } from './useDiscovery';

interface TrendingTagsProps {
  params: Record<string, string>;
  selected: string[];
  onToggle: (tag: string) => void;
}

/**
 * Spec §8.3. Narrowed to the selected place, because the global leader board is
 * the same five tags every day and tells a reader in Timișoara nothing.
 */
export function TrendingTags({ params, selected, onToggle }: TrendingTagsProps) {
  const { t } = useTranslation();
  const tags = useTrendingTags(params);

  if (tags.isError) return null;

  return (
    <Card sx={{ p: 2 }} component="section" aria-label={t('discovery.trending')}>
      <Stack spacing={1.25}>
        <Typography variant="overline" color="text.secondary">
          {t('discovery.trending')}
        </Typography>

        {tags.isPending ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {[68, 84, 56, 92, 72].map((width, index) => (
              <Skeleton key={index} variant="rounded" width={width} height={26} />
            ))}
          </Stack>
        ) : tags.data.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('discovery.noTags')}
          </Typography>
        ) : (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {tags.data.map((tag) => {
              const isSelected = selected.includes(tag.name);
              return (
                <Chip
                  key={tag.name}
                  size="small"
                  label={`#${tag.name}`}
                  onClick={() => onToggle(tag.name)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  aria-pressed={isSelected}
                  // The count belongs in the label a screen reader hears, not
                  // only in a tooltip nobody hovers on a phone.
                  aria-label={`${tag.name}, ${t('post.count', { count: tag.postCount })}`}
                />
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
